import angular from 'angular';
import * as R from 'ramda';
import { default as orgDataService } from '../data/org.data.service';

const library = require('@safe-systems/ss-ui-components');

const {
  CacheManager,
  permissionSetsService,
} = library;

let _$q;
let _$filter;

let _orgDataService;
let _permissionSetsService;
let _cacheManager;

class userService {
  constructor(
    $q,
    $filter,
    orgDataService, // eslint-disable-line no-shadow
    permissionSetsService // eslint-disable-line no-shadow
  ) {
    /* Private Members */
    _$q = $q;
    _$filter = $filter;
    _orgDataService = orgDataService;
    _permissionSetsService = permissionSetsService;
    _cacheManager = CacheManager.instance();

    /* Public Properties */
    /*
     * NOTE: The "legacy" properties below support the strategy
     * that clients access this service's properties directly, and must
     * watch the "dfdRefresh" promise to know when the properties
     * have values. This can result in accidental race conditions, hiding errors
     * in the original $http promise, and extra code complexity.
     *
     * The new way is for clients to not use these properties at all.
     * Instead, "smart components" (top level components) call these methods and get
     * the data they need in the .then() and put the resolved data into their scopes.
     * "Dumb components" should continue to receive scope properties through their attributes.
     *
     * Clients should avoid mutating the data received from these functions if at all possible.
     * Instead, look to see if you can add a function here that returns the changed state.
     * This service should end up having many getters and setters.
     */
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.user = null; // legacy
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.widgets = null; // legacy
    /**
     * @deprecated Use get method that returns a promise instead (may need to implement)
     */
    this.dfdRefreshed = $q.defer(); // legacy
    this.isLoggedOn = false;

    console.log('app.services.userService created');
  }

  /**
   * Retrieve token from response object
   * @returns {token}
   */
  _getToken(loginResponse, userName) {
    const { access_token, access_token_expires_in, expires_in } = loginResponse.data; // eslint-disable-line camelcase
    return {
      userName,
      access_token,
      access_token_expires_in,
      expires_in,
    };
  }


  /**
   * User object definition
   * @typedef {Object} user
   * @property {int} customerId
   * @property {string} servicesEula
   */

  /**
   * Resets user object and session data to initial state
   * @returns {void}
   */
  reset() {
    this.isLoggedOn = false;
    this.user = null; // legacy
    this.widgets = null; // legacy
    this.dfdRefreshed = _$q.defer(); // legacy
    _cacheManager.clearCache();
  }

  /**
   * Returns a cached promise, or fetches the portal data
   * @param {Boolean} forceRefresh Should API call be forced
   * @returns {Promise} API portal object
   */
  getPortal(forceRefresh = false) {
    if (forceRefresh || !_cacheManager.portal) {
      _cacheManager.portal = _orgDataService.getPortalInfo()
        .then(
          response => response.data
          // TODO: handle response errors and payload discrepancies
        );
    }
    return _cacheManager.portal;
  }

  /**
   * Returns a cached promise, or fetches the user data
   * @param {Boolean} forceRefresh Should API call be forced
   * @returns {Promise<user | never>} API user object from portal
   */
  getUser(forceRefresh = false) {
    return this.getPortal(forceRefresh)
      .then((portalData) => {
        const user = portalData.userInfo;

        if (!user || user.customerId.length === 0) {
          console.error('customerId is not defined');
          return _$q.reject('customerId is not defined');
        }
        this.user = user; // legacy
        this.dfdRefreshed.resolve(); // legacy
        return user;
      });
  }

  /**
   * Fetches the user data and refreshes the cache
   * @returns {Promise<user | never>} API user object from portal
   */
  refreshUser() {
    _cacheManager.clearCache();
    return this.getUser(true);
  }

  getCarouselItems() {
    return this.getPortal().then(portalData => portalData.carouselItems.result);
  }

  getJumpRightInItems() {
    return this.getPortal().then(portalData => portalData.jumpRightInItems.result);
  }

  getUnlockTheSafeItems() {
    return this.getPortal().then(portalData => portalData.unlockTheSafeItems.result);
  }

  getWidgets(forceRefresh = false) {
    if (forceRefresh || !_cacheManager.widgets) {
      _cacheManager.widgets = _orgDataService.getWidgets()
        .then(
          (response) => {
            const widgets = _$filter('orderBy')(response.data.result, 'currentSortOrder');

            this.widgets = widgets;
            return widgets;
          }
          // TODO: handle response errors and payload discrepancies
        );
    }
    return _cacheManager.widgets;
  }

  /* eslint-disable */
  getApplicationNotificationItems(forceRefresh = false) {
    return this.getPortal().then(portalData => portalData.applicationNotificationItems.result);
    // return this.getPortal(forceRefresh).then(portalData => portalData.applicationNotificationItems.result);
  }

  getSystemNotificationItems() {
    return this.getPortal().then(portalData => portalData.systemNotificationItems.result);
  }

  /**
   * ViewModel can bind directly to the promise which will resolve to the value
   * @public
   * @param preferenceName
   * @returns {Promise<T | never>} API user preferences object
   */
  getUserPreference(preferenceName) {
    return this.getUser().then(userData => R.find(
      R.propEq('preferenceName', preferenceName),
      userData.preferences
    ).preferenceValue);
  }

  getUserNotifications(forceRefresh = false) {
    if (forceRefresh || !_cacheManager.userNotifications) {
      _cacheManager.userNotifications = _orgDataService.getUserNotifications()
        .then(
          response => response.data
          // TODO: handle response errors and payload discrepancies
        );
    }
    return _cacheManager.userNotifications;
  }

  /**
   * DEPRECATED: Not async safe! Use getUserPermissionSet or getUserPermissionByAPIKey instead
   * Returns an individual user permission by name
   * @deprecated Use getUserPermissionSet or getUserPermissionByAPIKey instead
   * @public
   * @param {String} permissionName
   * @returns {boolean} Has the given permission
   */
  getUserPermission(permissionName) {
    let permissionValue = false;
    const permission = R.find(
      R.propEq('name', permissionName),
      this.user.permissions
    );

    if (!R.isNil(permission)) {
      permissionValue = permission.value;
    }
    return permissionValue;
  }

  /**
   * Returns an individual user permission by name (async safe)
   * @public
   * @param {String} permissionAPIKeyName Permission key name as recognized by the API
   * @returns {Promise<boolean | never>} API user permission
   */
  getUserPermissionAsync(permissionAPIKeyName) {
    return this.getUser().then((userData) => {
      let permissionValue = false;
      const permission = R.find(
        R.propEq('name', permissionAPIKeyName),
        userData.permissions
      );

      if (!R.isNil(permission)) {
        permissionValue = permission.value;
      }
      return permissionValue;
    });
  }

  /**
   * Looks up the permissionSetName and returns
   * the user's permissions mapped to that set
   * @public
   * @param {String} permissionSetName Name of the set in PermissionSetsService
   * @returns {Promise<any | never>} Returns hash collection of permissions with values
   */
  getUserPermissionSet(permissionSetName) {
    const permissionSet = _permissionSetsService.getPermissionSet(permissionSetName);

    return this.getUser().then((userData) => {
      const permissions = {};

      R.toPairs(permissionSet).forEach((item) => {
        const modelName = item[0];
        const apiName = item[1];
        const permission = R.find(
          R.propEq('name', apiName),
          userData.permissions
        );
        if (permission) {
          permissions[modelName] = permission.value;
        } else {
          permissions[modelName] = false;
        }
      });
      return permissions;
    });
  }

  combineUserRolesData(roles, userRoles) {
    return R.map((role) => {
      let newRole = R.clone(role);
      if (newRole.roleGroupId > 3) {
        newRole.isNavantaRole = false;
        newRole.isCustomerRole = true;
      } else {
        newRole.isNavantaRole = true;
        newRole.isCustomerRole = false;
      }
      const userRole = R.find(
        R.propEq(
          'roleId',
          role.roleId
        ),
        userRoles
      );
      if (userRole) {
        newRole = R.merge(
          newRole,
          userRole
        );
        newRole.userHasAccess = true;
      } else {
        newRole.userHasAccess = false;
      }
      return newRole;
    }, roles);
  }

  combineUserCustomersData(customers, userCustomers) {
    return R.map((customer) => {
      const newCustomer = R.clone(customer);
      newCustomer.isSelected = false;

      const userCustomer = R.find(R.propEq('customerId', customer.customerId), userCustomers);

      if (userCustomer) {
        newCustomer.isSelected = true;
      }

      return newCustomer;
    }, customers);
  }
}

userService.$inject = [
  '$q',
  '$filter',

  'orgDataService',
  'permissionSetsService',
];

export default angular.module('app.services.userService', [
  orgDataService,
  permissionSetsService,
]).service('userService', userService)
  .name;
